/**
 * R5 — ListinoAlternativesPopover.jsx
 *
 * When a voce has _is_custom or zero candidati, operators can click a
 * "Alternative" button to pop a list of comparable catalogue products
 * (DN±1, PN±1, material equivalents). Each has a Swap button that patches
 * the voce params.
 */
import { useEffect, useState } from "react";
import { Loader2, Package, ArrowRightLeft, Search } from "lucide-react";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "./ui/popover";
import { toast } from "sonner";
import { api, formatErrorDetail } from "../lib/api";

/**
 * @param props.voce          — voce currently failing
 * @param props.onSwap(patch) — called with {parametri_estratti_patch}
 * @param props.triggerLabel  — custom label for the trigger button
 * @param props.disabled
 */
export default function ListinoAlternativesPopover({
  voce,
  onSwap,
  triggerLabel = "Alternative",
  disabled = false,
}) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState([]);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open || !voce?.famiglia) return;
    setLoading(true);
    setError("");
    const target = {
      ...(voce.parametri_estratti || {}),
      ...(voce.parametri_inferiti || {}),
    };
    api
      .listinoAlternatives(voce.famiglia, target, 6)
      .then((d) => setItems(d.items || []))
      .catch((e) => {
        setError(formatErrorDetail(e));
        setItems([]);
      })
      .finally(() => setLoading(false));
  }, [open, voce?.famiglia, voce?.idx]);

  const doSwap = (alt) => {
    if (!alt || !onSwap) return;
    const patch = { ...(alt.target_swap || {}) };
    onSwap(patch, alt);
    toast.success(`Voce aggiornata: ${alt.codice}`);
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          disabled={disabled}
          className="inline-flex items-center gap-1 h-6 px-1.5 rounded text-[11px] font-medium transition-colors disabled:opacity-40"
          style={{
            color: "var(--accent-primary)",
            background: "transparent",
            border: "1px solid #DBEAFE",
          }}
          onMouseEnter={(e) => {
            if (!disabled)
              e.currentTarget.style.background = "var(--accent-subtle)";
          }}
          onMouseLeave={(e) =>
            (e.currentTarget.style.background = "transparent")
          }
          data-testid="alternatives-trigger"
        >
          <Search className="h-3 w-3" />
          {triggerLabel}
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="w-[420px] p-0"
        data-testid="alternatives-popover"
      >
        <div
          className="px-3 py-2 border-b"
          style={{ borderColor: "var(--border-subtle)" }}
        >
          <div className="text-[12px] font-semibold"
               style={{ color: "var(--text-primary)" }}>
            Alternative a listino
          </div>
          <div className="text-[11px]"
               style={{ color: "var(--text-muted)" }}>
            {voce?.famiglia} · {voce?.sotto_tipo || "—"}
          </div>
        </div>
        <div className="max-h-[320px] overflow-auto">
          {loading && (
            <div className="px-3 py-6 flex items-center gap-2 text-[12px]"
                 style={{ color: "var(--text-muted)" }}>
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
              Ricerca alternative in corso…
            </div>
          )}
          {!loading && error && (
            <div className="px-3 py-4 text-[12px]"
                 style={{ color: "#B91C1C" }}>
              {error}
            </div>
          )}
          {!loading && !error && items.length === 0 && (
            <div className="px-3 py-6 text-center text-[12px]"
                 style={{ color: "var(--text-muted)" }}>
              <Package className="h-5 w-5 mx-auto mb-1.5 opacity-50" />
              Nessuna alternativa a listino per questi parametri.
            </div>
          )}
          {!loading && !error && items.length > 0 && (
            <ul className="divide-y"
                style={{ borderColor: "var(--border-subtle)" }}
                data-testid="alternatives-list">
              {items.map((it) => (
                <li
                  key={it.codice}
                  className="px-3 py-2 hover:bg-slate-50 transition-colors"
                  data-testid={`alternative-${it.codice}`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      <div className="font-mono text-[12px] font-semibold"
                           style={{ color: "var(--text-primary)" }}>
                        {it.codice}
                      </div>
                      <div
                        className="text-[11px] truncate"
                        style={{ color: "var(--text-secondary)" }}
                        title={it.descrizione}
                      >
                        {it.descrizione}
                      </div>
                      <div
                        className="text-[10px] mt-0.5"
                        style={{ color: "var(--text-muted)" }}
                      >
                        {it.diff_label}
                      </div>
                    </div>
                    <div className="shrink-0 text-right">
                      <div className="tq-cell-mono text-[12px] font-semibold">
                        {it.prezzo != null ? `€ ${Number(it.prezzo).toFixed(2)}` : "—"}
                      </div>
                      <button
                        type="button"
                        onClick={() => doSwap(it)}
                        className="mt-1 inline-flex items-center gap-1 px-2 h-6 rounded text-[11px] font-medium text-white transition-colors"
                        style={{ background: "var(--accent-primary)" }}
                        data-testid={`swap-${it.codice}`}
                      >
                        <ArrowRightLeft className="h-3 w-3" />
                        Sostituisci
                      </button>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
